<script setup lang="ts">
/**
 * A person as a chip: avatar (photo, initials, presence dot) and optionally
 * the name. Opens the same card as `NxEntityChip` with the e-mail address as
 * subtitle; the actions (message, mail, profile, copy address) come from the
 * caller through `#actions`, which gets `close()`.
 *
 * Defaults fit an avatar-only person in a list (28 px, no name); all other
 * behaviour — open on click or only on right click, `href`/`to`, `activate`
 * on double click — is `NxEntityChip`'s.
 */
import { computed, ref } from 'vue';
import type { RouteLocationRaw } from 'vue-router';
import type { NxAvatarStatus } from '../Avatar/NxAvatar.vue';
import type { NxPopoverSide } from '../Popover/NxPopover.vue';
import NxEntityChip from './NxEntityChip.vue';
import type { NxEntityChipDetail } from './types';

const props = withDefaults(
  defineProps<{
    name: string;
    email?: string;
    /** Second card line; default: the e-mail address. */
    subtitle?: string;
    /** Photo URL. */
    src?: string;
    /** Explicit initials or short name („JD“). */
    initials?: string;
    status?: NxAvatarStatus;
    details?: readonly NxEntityChipDetail[];
    badge?: string;
    size?: number;
    showName?: boolean;
    openOn?: 'click' | 'contextmenu';
    href?: string;
    to?: RouteLocationRaw;
    open?: boolean;
    side?: NxPopoverSide;
    emptyText?: string;
  }>(),
  {
    email: undefined,
    subtitle: undefined,
    src: undefined,
    initials: undefined,
    status: undefined,
    details: () => [],
    badge: undefined,
    size: 28,
    showName: false,
    openOn: 'click',
    href: undefined,
    to: undefined,
    open: undefined,
    side: 'bottom-start',
    emptyText: '—',
  }
);

const emit = defineEmits<{
  'update:open': [open: boolean];
  activate: [event: MouseEvent];
}>();

defineSlots<{
  icon?(props: { size: number }): unknown;
  card?(): unknown;
  actions?(props: { close: () => void }): unknown;
}>();

const chip = ref<InstanceType<typeof NxEntityChip> | null>(null);
const label = computed(() => props.name?.trim() || props.email?.trim() || '');
const cardSubtitle = computed(
  () => props.subtitle ?? (props.email && props.email !== label.value ? props.email : undefined)
);

defineExpose({ open: () => chip.value?.open(), close: () => chip.value?.close() });
</script>

<template>
  <NxEntityChip
    ref="chip"
    class="nx-person-chip"
    :label="label"
    :subtitle="cardSubtitle"
    :details="details"
    :badge="badge"
    :src="src"
    :initials="initials"
    :status="status"
    shape="circle"
    :size="size"
    :show-name="showName"
    :open-on="openOn"
    :href="href"
    :to="to"
    :open="open"
    :side="side"
    :empty-text="emptyText"
    @update:open="(v) => emit('update:open', v)"
    @activate="(e) => emit('activate', e)"
  >
    <template v-if="$slots.icon" #icon="{ size: iconSize }">
      <slot name="icon" :size="iconSize" />
    </template>
    <template v-if="$slots.card" #card>
      <slot name="card" />
    </template>
    <template v-if="$slots.actions" #actions="{ close }">
      <slot name="actions" :close="close" />
    </template>
  </NxEntityChip>
</template>
